/**
 * Módulo del tutorial: Lluvias (KAN-232).
 *
 * El negocio del pasto: registrar los mm del pluviómetro (modal completo:
 * fecha, milímetros, guardar — el botón avanza), ver el acumulado del año, y
 * comparar contra la lluvia SATelital por año en la tabla "Tu registro vs el
 * satélite" — años secos vs húmedos en tu propio campo.
 *
 * Anclas: menu-lluvias, lluvias-btn-registrar, lluvia-form-fecha/mm,
 * lluvia-btn-guardar, lluvias-stat-anio, lluvias-card-satelital.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const lluviasModule: TutorialModuleDef = {
  id: 'lluvias',
  title: 'Lluvias: el negocio del pasto',
  description: 'Registrá los milímetros del pluviómetro y compará con la lluvia satelital por año — años secos vs húmedos en tu propio campo.',
  emoji: '🌧️',
  image: 'images/tutorial/lluvias.png',
  permission: { module: 'tools', action: 'create' },
  grupo: 'produccion',
  route: '/lluvias',
  steps: [
    {
      id: 'concepto-lluvias',
      route: '/tutorial',
      text: 'En el campo, la lluvia ES el negocio: más mm, más pasto; más pasto, más hacienda. Acá la anotás y la comparás por año — y además alimenta la estimación de pasto de los cuadros en el mapa.',
      narration:
        'Hablemos del socio silencioso del negocio: la lluvia. Más milímetros, más pasto; más pasto, más kilos. En esta sección la anotás lluvia por lluvia, y lo mejor: la comparás por año, en tu propio campo, no en el del vecino. Y tiene un segundo trabajo del que te aviso: la lluvia que registrás alimenta la estimación de pasto de los cuadros en la sección del mapa, para calcular cuántas raciones da cada uno. La historia del clima de tu tierra, contada por números, y trabajando a tu favor.',
    },
    {
      id: 'ir-a-lluvias',
      target: '[data-ar-guide="menu-lluvias"]',
      label: 'Lluvias (menú Principal)',
      text: 'En el menú, sección Principal, tocá "Lluvias" (en celular: menú ☰).',
      narration:
        'Andá al menú, sección Principal, y tocá Lluvias. Ahí te esperan los acumulados del año a la vista, y el botón para anotar la última caída.',
    },
    {
      id: 'registrar-lluvia',
      route: '/lluvias',
      target: '[data-ar-guide="lluvias-btn-registrar"]',
      label: 'Registrar lluvia',
      text: 'Llovió? Tocá "Registrar lluvia" y se abre el formulario — vamos a cargarla juntos.',
      narration:
        'Cada vez que llueve, un momento: tocá Registrar lluvia y se abre el formulario. Te lo muestro andando, que es de lo más simple que tiene la app.',
    },
    {
      id: 'lluvia-fecha',
      route: '/lluvias',
      target: '[data-ar-guide="lluvia-form-fecha"]',
      label: 'Fecha de la lluvia',
      advanceOnClick: false,
      text: 'La fecha: por defecto trae HOY — si estás anotando la lluvia de ayer, cambiala.',
      narration:
        'Primero la fecha: ya trae el día de hoy, que es lo normal, porque lo ideal es anotar la lluvia el mismo día. Si te quedó deber una de ayer o de anteayer, la cambiás acá nomás.',
    },
    {
      id: 'lluvia-mm',
      route: '/lluvias',
      target: '[data-ar-guide="lluvia-form-mm"]',
      label: 'Milímetros caídos',
      advanceOnClick: false,
      text: 'Los milímetros que marcó el pluviómetro — "12,5" por ejemplo. Ese es todo el dato.',
      narration:
        'Y el número de la cuestión: los milímetros que marcó el pluviómetro. Doce y medio, treinta, lo que haya caído. Ese es todo el dato que te pide; cinco segundos y quedó.',
    },
    {
      id: 'lluvia-guardar',
      route: '/lluvias',
      target: '[data-ar-guide="lluvia-btn-guardar"]',
      label: 'Guardar registro',
      text: 'Tocá "Guardar registro" — y cada lluvia suma sola al acumulado del año.',
      narration:
        'Apretá Guardar registro, y listo: esa lluvia ya está en la historia de tu campo, sumando al acumulado del año. Sin planilla empapada en el alambrado.',
    },
    {
      id: 'acumulado-anio',
      route: '/lluvias',
      target: '[data-ar-guide="lluvias-stat-anio"]',
      label: 'Acumulado del año',
      advanceOnClick: false,
      text: '¿Para qué sirve? Mirá arriba: el acumulado del AÑO se arma solo con cada registro — cuántos mm cayeron y cuántas lluvias anotaste, a la vista.',
      narration:
        '¿Y para qué tanto rigor? Mirá este número de arriba: el acumulado del año, que se arma solo con cada registro que cargás. Al lado te dice cuántas lluvias llevás anotadas. Ese número es el termómetro del pasto: cuando la gente de la zona pregunta cómo viene el año, vos tenés la respuesta exacta de tu campo.',
    },
    {
      id: 'lluvia-satelital',
      route: '/lluvias',
      target: '[data-ar-guide="lluvias-card-satelital"]',
      label: '🛰️ Tu registro vs el satélite',
      advanceOnClick: false,
      text: 'Expandí "🛰️ Tu registro vs el satélite": por cada año, lo que REGISTRASTE contra la ESTIMACIÓN satelital de tu zona. Los ⚠️ son meses que capaz se te olvidó anotar — y tu pluviómetro siempre manda.',
      narration:
        'Y acá está el contralor cruzado: esta tarjeta que se despliega, Tu registro contra el satélite. Por cada año te muestra cuánto registraste vos contra cuánto estima el satélite para tu zona. ¿Ves algún añito con señal de alerta? Son meses donde el satélite dice que llovió y vos no tenés anotado: capaz se te olvidó. Es una estimación de lejos, orientativa: cuando hay diferencia, tu pluviómetro manda, siempre.',
    },
    {
      id: 'comparar-anios',
      route: '/lluvias',
      text: 'Y la perla: en esa misma tabla comparás AÑOS — el seco contra el húmedo, totales de cada año a la vista, para saber cuánta agua de verdad tiene tu campo.',
      narration:
        'Y la perla de la sección está en esa misma tabla: comparar años. El seco contra el húmedo, totales año por año, uno debajo del otro. Así sabés cuánta agua de verdad cae en tu campo, y podés decidir carga y época de service con el clima de tu lado. El pasado, al servicio del próximo año.',
    },
  ],
  completionCheck: async () => false,
};
